import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(0);
  const [selectedSkills, setSelectedSkills] = useState(['pronunciation', 'fluency']);

  const toggleSkill = (skill) => {
    setSelectedSkills(prev => 
      prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]
    );
  };

  const faqs = [
    {
      q: "What is VocaAI?",
      a: "VocaAI is your personal AI English tutor that helps you improve fluency, pronunciation, grammar, and vocabulary through real-time speaking practice. Think of it as a human tutor, but available 24/7 and 20x cheaper."
    },
    { q: "Who is VocaAI for?", a: "It's for anyone looking to improve their English speaking confidence, from B1 intermediate to C2 advanced learners." },
    { q: "How is VocaAI different from other English apps?", a: "Unlike flashcard apps, VocaAI focuses entirely on live, conversational speaking practice with instant, personalized feedback." },
    { q: "How much time can I practice English with the VocaAI tutor?", a: "You get unlimited practice! You can talk to your AI tutor as much as you want." },
    { q: "Does VocaAI adapt to my level and interests?", a: "Yes, the AI analyzes your initial conversations to gauge your level and adjusts its vocabulary, speed, and topics accordingly." }
  ];

  return (
    <div className="landing-wrapper">
      {/* Navbar */}
      {/* Navbar */}
      <nav className="landing-navbar">
        <div className="nav-logo" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* ✅ Use "/logo.png" or "/logo.svg" depending on your exact file name */}
          <img 
            src="/logo.png" 
            alt="VocaAI Logo" 
            style={{ width: '120px', height: '120px', objectFit: 'contain' }} 
          />
          VocaAI
        </div>
        {/* ... rest of your nav ... */}
        <div className="nav-actions">
          <span className="trustpilot-rating">
            Excellent <strong>4.7</strong> out of 5 <span className="star">★</span> Trustpilot
          </span>
          <select className="lang-select">
            <option>🌐 English</option>
          </select>
          <button className="nav-login-btn" onClick={() => navigate('/onboarding')}>Log in</button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-glow"></div>
        <div className="avatar-container">
          <img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="hero-avatar" />
        </div>
        <h1 className="hero-title">
          Improve your English speaking<br/>
          with a <span className="text-blue">personal</span> AI tutor
        </h1>
        <p className="hero-subtitle">
          <span className="text-orange">VocaAI is 20x cheaper than a human English tutor</span><br/>
          and always ready for speaking practice
        </p>
        <button className="cta-button" onClick={() => navigate('/onboarding')}>
          Improve my English
        </button>
        
        <div className="trusted-by">
          <span><strong>Y</strong> Combinator</span>
          <span>PIONEER FUND</span>
          <span>TechCrunch</span>
        </div>
      </section>

      {/* Feature 1 */}
      <section className="feature-section">
        <div className="feature-text">
          <h2>Track your real level of English</h2>
          <p>VocaAI evaluates your pronunciation, grammar, vocabulary, and fluency so you understand where you stand and how to improve.</p>
        </div>
        <div className="feature-image-placeholder">
          <div className="mock-card">
            <p className="mock-level">Proficient <span className="text-blue">C2</span></p>
            <div className="mock-radar"></div>
          </div>
        </div>
      </section>

      {/* Feature 2 */}
      <section className="feature-section reverse">
        <div className="feature-text">
          <h2>Improve with your personal learning plan</h2>
          <p>Our AI tutor analyzes your mistakes and adapts each lesson to your level and goals, so you can learn English naturally and with topics that interest you.</p>
        </div>
        <div className="feature-image-placeholder">
          <div className="mock-card">
            <p className="mock-level">How to reach <span className="text-blue">C1</span></p>
            <div className="mock-bars">
              <div className="bar-group"><span>Grammar</span><div className="bar"><div className="fill grammar" style={{width: '25%'}}></div></div></div>
              <div className="bar-group"><span>Prepositions</span><div className="bar"><div className="fill prepositions" style={{width: '60%'}}></div></div></div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature 3 */}
      <section className="feature-section">
        <div className="feature-text">
          <h2>Get unlimited English speaking practice</h2>
          <p>VocaAI helps you speak English confidently in interviews, meetings, sales, presentations, and more, by learning in a practical and natural way.</p>
        </div>
        <div className="feature-image-placeholder">
          <div className="mock-card circle-layout">
             <div className="hero-glow purple-glow"></div>
             <img src="https://i.pravatar.cc/150?img=47" alt="AI Tutor" className="hero-avatar small" />
             <div className="floating-bubble b1">Pitch</div>
             <div className="floating-bubble b2">Free talk</div>
             <div className="floating-bubble b3">Let's make a mock interview!</div>
          </div>
        </div>
      </section>

      {/* Skills Interactive Section */}
      <section className="skills-section">
        <div className="skills-glow"></div>
        <h2>What would you like<br/>to <span className="text-blue">improve</span> in your English?</h2>
        <p>Pick skills to practice with your AI tutor:</p>
        
        <div className="skills-grid">
          {[
            { id: 'pronunciation', icon: '🎙️', label: 'pronunciation' },
            { id: 'grammar', icon: '✏️', label: 'grammar' },
            { id: 'vocabulary', icon: '📖', label: 'vocabulary' },
            { id: 'fluency', icon: '〰️', label: 'fluency' }
          ].map(skill => (
            <div 
              key={skill.id} 
              className={`skill-card ${selectedSkills.includes(skill.id) ? 'active' : ''}`}
              onClick={() => toggleSkill(skill.id)}
            >
              <span className="skill-icon">{skill.icon}</span>
              <span className="skill-label">{skill.label}</span>
              <div className="radio-circle"></div>
            </div>
          ))}
        </div>
        <button className="cta-button white-btn" onClick={() => navigate('/onboarding')}>Let's improve</button>
      </section>

      {/* Integrations Section */}
      <section className="integrations-section">
        <div className="integrations-container">
          <h2>Get AI feedback<br/>on your <span className="text-blue">real-life</span> calls</h2>
          <p>Connect VocaAI to your online calls to fix mistakes in your grammar, pronunciation, and vocabulary.</p>
          
          <div className="apps-row">
            <div className="app-icon meet">G</div>
            <div className="app-icon telegram">T</div>
            <div className="app-icon whatsapp">W</div>
            <div className="app-icon meet">G</div>
            <div className="app-icon telegram">T</div>
            <div className="app-icon whatsapp">W</div>
            <div className="app-icon teams">T</div>
            <div className="app-icon discord">D</div>
            <div className="app-icon skype">S</div>
          </div>
          <p className="apps-note">VocaAI supports every meeting platform</p>
        </div>
      </section>

      {/* Social Proof & Value Section */}
      <section className="value-section">
        <div className="value-glow"></div>
        <h2>Join our users<br/><span className="text-blue">from 239 countries</span></h2>
        
        <div className="reviews-row">
          <div className="review-card">
            <div className="stars">★★★★★</div>
            <h4>I like this app</h4>
            <p>Everything is great. Very helpful application.</p>
            <span className="reviewer">Liudmyla, 1 hour ago</span>
          </div>
          <div className="review-card">
            <div className="stars">★★★★★</div>
            <h4>Great app for improving...</h4>
            <p>Great app for improving English. The AI makes learning interactive...</p>
            <span className="reviewer">Reda, 7 hours ago</span>
          </div>
          <div className="review-card">
            <div className="stars">★★★★★</div>
            <h4>I had a fantastic experience...</h4>
            <p>I had a fantastic experience with VocaAI! This AI has boosted my...</p>
            <span className="reviewer">Olga, 8 hours ago</span>
          </div>
        </div>

        <div className="huge-value">
          <h1 className="bg-text">20X</h1>
          <h3>...<span className="text-blue">cheaper</span> than<br/>a human tutor.</h3>
          <p>And available 24/7</p>
          
          <div className="value-badges">
            <span className="badge">✓ Fix the grammar</span>
            <span className="badge">✓ Reduce accent</span>
            <span className="badge">✓ Improve speaking</span>
            <span className="badge">✓ Expand vocabulary</span>
          </div>
          
          <button className="cta-button blue-glow-btn" onClick={() => navigate('/onboarding')}>
            Try VocaAI
          </button>
        </div>
      </section>

      {/* Test Level Section */}
      <section className="test-level-section">
        <h2>Get your <span className="text-purple">real</span> English level</h2>
        <p>Make a <span className="text-purple underline">quick 4-minute call</span> with our AI tutor<br/>and get your English score for FREE</p>
        
        <div className="test-card-mockup">
           <div className="level-bar-mockup">
             <span>A1</span><span>A2</span><span>B1</span><span>B2</span><span className="active-c1">C1</span><span className="dim">C2</span>
           </div>
           <p className="mock-level">Proficient <span className="text-blue">C1</span></p>
           <div className="mock-radar small"></div>
           <button className="dark-test-btn" onClick={() => navigate('/onboarding')}>
             Test My English Level <span>→</span>
           </button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="faq-container">
          <div className="faq-header">
            <h2>Questions & Answers</h2>
            <p>Have more questions? Feel free to email us:<br/><a href="mailto:team@vocaai.app">team@vocaai.app</a></p>
            <h1 className="faq-bg-text">FAQ</h1>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`faq-item ${activeFaq === index ? 'active' : ''}`}
                onClick={() => setActiveFaq(activeFaq === index ? -1 : index)}
              >
                <div className="faq-q">
                  <span className="faq-num">0{index + 1}</span>
                  {faq.q}
                  <span className="faq-toggle">{activeFaq === index ? '∧' : '∨'}</span>
                </div>
                {activeFaq === index && <div className="faq-a">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-col brand-col">
          <div className="nav-logo"><span className="logo-icon">»</span> VocaAI</div>
          <p className="copyright">Copyright © 2026 VocaAI inc.</p>
          <select className="lang-select small">
            <option>🌐 English</option>
          </select>
          <div className="trustpilot-rating small mt-1">
            See our <strong>1,202 reviews</strong> on <span className="star">★</span> Trustpilot
          </div>
        </div>
        <div className="footer-col">
          <h4>Company</h4>
          <a href="#support">Support</a>
          <a href="#privacy">Privacy Policy</a>
          <a href="#terms">Terms of Service</a>
        </div>
        <div className="footer-col">
          <h4>Social Media</h4>
          <a href="#ig">Instagram</a>
          <a href="#li">LinkedIn</a>
          <a href="#tw">Twitter</a>
        </div>
        <div className="footer-col">
          <h4>Resources</h4>
          <a href="#blog">Blog</a>
          <a href="#ios">VocaAI iOS app</a>
          <a href="#android">VocaAI Android app</a>
          <a href="#test">Free Accent Test</a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;